import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { 
  BarChart3, Users, MousePointerClick, Flame, Trophy, 
  TrendingUp, DollarSign, Share2, BookOpen, RefreshCw,
  Download, Mail, Eye, Target, UserPlus, Activity,
  Calendar, Filter, ChevronDown, FileText, Database, Search
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [retention, setRetention] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [events, setEvents] = useState(null);
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [eventDays, setEventDays] = useState(7);

  const [members, setMembers] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberSort, setMemberSort] = useState('created_at');
  const [memberOrder, setMemberOrder] = useState('desc');
  const [memberPage, setMemberPage] = useState(1);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (activeTab === 'events') {
      fetchEvents();
    }
  }, [eventDays]);

  useEffect(() => {
    if (activeTab === 'members') fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, memberSearch, memberSort, memberOrder, memberPage]);

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API}/admin/members`, {
        params: { search: memberSearch, sort: memberSort, order: memberOrder, page: memberPage, page_size: 20 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers(res.data);
    } catch (error) {
      toast.error('Failed to load members');
    }
  };

  const toggleSort = (field) => {
    if (memberSort === field) {
      setMemberOrder(memberOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setMemberSort(field);
      setMemberOrder('desc');
    }
    setMemberPage(1);
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, revenueRes, retentionRes, funnelRes, demographicsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/revenue-report`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/retention`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/funnel`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/demographics`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/leads/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setData(dashboardRes.data);
      setRevenueData(revenueRes.data);
      setRetention(retentionRes.data);
      setFunnel(funnelRes.data);
      setDemographics(demographicsRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/analytics/events/summary?days=${eventDays}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(res.data);
    } catch (error) {
      console.error('Failed to fetch events');
    }
  };

  const handleExport = async (type) => {
    try {
      let url = '';
      let filename = '';
      
      switch(type) {
        case 'users':
          url = `${API}/export/csv/users`;
          filename = 'randrush_users.csv';
          break;
        case 'leads':
          url = `${API}/export/csv/leads`;
          filename = 'randrush_leads.csv';
          break;
        case 'users_json':
          url = `${API}/export/users`;
          filename = 'randrush_users.json';
          break;
        case 'events':
          url = `${API}/export/events?days=30`;
          filename = 'randrush_events.json';
          break;
        default:
          return;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create download
      const blob = new Blob([type.includes('csv') ? response.data : JSON.stringify(response.data, null, 2)], {
        type: type.includes('csv') ? 'text/csv' : 'application/json'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="text-[#C0975C] text-xl animate-pulse">Loading analytics...</div>
        </div>
      </Layout>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'members', label: 'Members', icon: UserPlus },
    { id: 'retention', label: 'Retention', icon: Users },
    { id: 'funnel', label: 'Funnel', icon: Target },
    { id: 'events', label: 'Events', icon: Activity },
    { id: 'leads', label: 'Leads', icon: Mail },
    { id: 'export', label: 'Export', icon: Download },
  ];

  return (
    <Layout>
      <div className="space-y-6 p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="text-[#0A1F44]">ANALYTICS</span> DASHBOARD
            </h1>
            <p className="text-[#A3A3A3] mt-1">Track user engagement, retention, and growth</p>
          </div>
          <button
            onClick={fetchAllData}
            className="neo-button px-4 py-2 flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-white/10 pb-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'events') fetchEvents();
                }}
                className={`px-4 py-2 flex items-center gap-2 text-sm font-bold transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-[#C0975C] text-black' 
                    : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard 
                icon={Users} 
                label="Total Users" 
                value={data?.users?.total || 0}
                subtext={`${data?.users?.active_week || 0} active this week`}
                color="#C0975C"
              />
              <MetricCard 
                icon={TrendingUp} 
                label="Retention Rate" 
                value={`${data?.users?.retention_rate || 0}%`}
                subtext="Weekly active / Total"
                color="#C0975C"
              />
              <MetricCard 
                icon={BookOpen} 
                label="Quizzes Completed" 
                value={data?.engagement?.total_quizzes_completed || 0}
                subtext={`${data?.engagement?.avg_quizzes_per_user || 0} avg/user`}
                color="#0A1F44"
              />
              <MetricCard 
                icon={Mail} 
                label="Leads Captured" 
                value={leads?.total_leads || 0}
                subtext={`${leads?.conversion_rate || 0}% converted`}
                color="#C0975C"
              />
            </div>

            {/* Content Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="neo-card p-4">
                <div className="text-3xl font-black text-[#C0975C]">{data?.content?.quiz_questions}</div>
                <div className="text-[#A3A3A3] text-sm">Quiz Questions</div>
              </div>
              <div className="neo-card p-4">
                <div className="text-3xl font-black text-[#0A1F44]">{data?.content?.budget_scenarios + data?.content?.business_scenarios}</div>
                <div className="text-[#A3A3A3] text-sm">Simulations</div>
              </div>
              <div className="neo-card p-4">
                <div className="text-3xl font-black text-[#C0975C]">55</div>
                <div className="text-[#A3A3A3] text-sm">Courses</div>
              </div>
              <div className="neo-card p-4">
                <div className="text-3xl font-black text-[#C0975C]">{data?.shares?.total || 0}</div>
                <div className="text-[#A3A3A3] text-sm">Social Shares</div>
              </div>
            </div>

            {/* Revenue & Referrals */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="neo-card p-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#C0975C]" />
                  Revenue Potential
                </h3>
                <div className="text-4xl font-black text-[#C0975C] mb-2">
                  {revenueData?.estimated_revenue}
                </div>
                <p className="text-[#A3A3A3] text-sm mb-4">{revenueData?.note}</p>
                
                <h4 className="font-bold mb-2">Product Performance</h4>
                <div className="space-y-2">
                  {revenueData?.product_performance?.slice(0, 5).map((p, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-[#A3A3A3]">{p.product_name}</span>
                      <span className="text-[#C0975C]">{p.total_clicks} clicks</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="neo-card p-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#C0975C]" />
                  Level Distribution
                </h3>
                <div className="space-y-2">
                  {data?.levels?.distribution?.slice(0, 8).map((level, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-16 text-sm text-[#A3A3A3]">Level {level._id}</span>
                      <div className="flex-1 h-4 bg-white/10">
                        <div 
                          className="h-full bg-gradient-to-r from-[#C0975C] to-[#C0975C]"
                          style={{ width: `${(level.count / data?.users?.total * 100)}%` }}
                        />
                      </div>
                      <span className="w-10 text-right text-sm">{level.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Users */}
            <div className="neo-card p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[#0A1F44]" />
                Recent Signups
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#A3A3A3] text-left">
                      <th className="pb-2">Username</th>
                      <th className="pb-2">Level</th>
                      <th className="pb-2">XP</th>
                      <th className="pb-2">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.recent_users?.map((user, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="py-2 font-bold">{user.username}</td>
                        <td className="py-2 text-[#C0975C]">{user.level}</td>
                        <td className="py-2">{user.xp}</td>
                        <td className="py-2 text-[#A3A3A3]">{user.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'members' && (
          <div className="space-y-4" data-testid="admin-members-tab">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[#C0975C]" />
                Members {members?.total ? `(${members.total})` : ''}
              </h3>
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                <input
                  value={memberSearch}
                  onChange={(e) => { setMemberSearch(e.target.value); setMemberPage(1); }}
                  placeholder="Search name or email"
                  data-testid="member-search-input"
                  className="w-full bg-[#061530] border border-[#1D3D72] rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:border-[#C0975C] outline-none"
                />
              </div>
            </div>

            <div className="neo-card p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="members-table">
                <thead>
                  <tr className="text-[#A3A3A3] text-left border-b border-white/10">
                    <SortableTh label="Username" field="username" sort={memberSort} order={memberOrder} onSort={toggleSort} />
                    <SortableTh label="Email" field="email" sort={memberSort} order={memberOrder} onSort={toggleSort} />
                    <SortableTh label="Level" field="level" sort={memberSort} order={memberOrder} onSort={toggleSort} />
                    <SortableTh label="XP" field="xp" sort={memberSort} order={memberOrder} onSort={toggleSort} />
                    <SortableTh label="Quizzes" field="total_quizzes_completed" sort={memberSort} order={memberOrder} onSort={toggleSort} />
                    <SortableTh label="Joined" field="created_at" sort={memberSort} order={memberOrder} onSort={toggleSort} />
                    <th className="px-4 py-3 font-semibold">Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {members?.members?.map((m, i) => (
                    <tr key={m.id || i} data-testid="member-row" className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3 font-bold text-white">
                        {m.username}
                        {m.is_admin && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#C0975C] text-[#0A1F44] px-1.5 py-0.5 rounded">Admin</span>}
                        {typeof m.age === 'number' && m.age < 18 && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#FF3333] text-white px-1.5 py-0.5 rounded" data-testid="under18-flag">Under 18</span>}
                      </td>
                      <td className="px-4 py-3 text-[#94A9C9]">{m.email}</td>
                      <td className="px-4 py-3 text-[#C0975C]">{m.level}</td>
                      <td className="px-4 py-3">{m.xp}</td>
                      <td className="px-4 py-3">{m.total_quizzes_completed || 0}</td>
                      <td className="px-4 py-3 text-[#A3A3A3]">{m.created_at?.slice(0, 10) || '-'}</td>
                      <td className="px-4 py-3 text-[#A3A3A3]">{(m.last_login || m.last_activity_date)?.slice(0, 10) || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {members && members.members.length === 0 && (
                <div className="p-8 text-center text-[#A3A3A3]" data-testid="members-empty">No members found</div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-[#A3A3A3]">Page {members?.page || 1} of {members?.total_pages || 1}</span>
              <div className="flex gap-2">
                <button
                  disabled={(members?.page || 1) <= 1}
                  onClick={() => setMemberPage((p) => Math.max(1, p - 1))}
                  data-testid="member-prev-btn"
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  disabled={(members?.page || 1) >= (members?.total_pages || 1)}
                  onClick={() => setMemberPage((p) => p + 1)}
                  data-testid="member-next-btn"
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-[#C0975C] text-[#0A1F44] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'retention' && retention && (
          <div className="space-y-6">
            {/* Retention Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard icon={Users} label="DAU" value={retention.dau} subtext={`${retention.dau_rate}% of total`} color="#C0975C" />
              <MetricCard icon={Calendar} label="WAU" value={retention.wau} subtext={`${retention.wau_rate}% of total`} color="#C0975C" />
              <MetricCard icon={TrendingUp} label="MAU" value={retention.mau} subtext={`${retention.mau_rate}% of total`} color="#0A1F44" />
              <MetricCard icon={Flame} label="Streak Active" value={retention.streak_active} subtext="3+ day streaks" color="#C0975C" />
            </div>

            {/* Churn */}
            <div className="neo-card p-6">
              <h3 className="text-lg font-bold mb-4">Churn Analysis</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-5xl font-black text-red-500">{retention.churn_rate}%</div>
                  <div className="text-[#A3A3A3]">Churn Rate (30 days)</div>
                </div>
                <div>
                  <div className="text-5xl font-black text-[#A3A3A3]">{retention.churned}</div>
                  <div className="text-[#A3A3A3]">Inactive Users</div>
                </div>
              </div>
            </div>

            {/* Age Demographics */}
            {demographics && (
              <div className="neo-card p-6">
                <h3 className="text-lg font-bold mb-4">Age Distribution</h3>
                <div className="flex flex-wrap gap-2">
                  {demographics.age_distribution?.map((age, i) => (
                    <div key={i} className="bg-white/5 px-4 py-2 text-center">
                      <div className="text-xl font-black text-[#C0975C]">{age.count}</div>
                      <div className="text-xs text-[#A3A3A3]">Age {age._id}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'funnel' && funnel && (
          <div className="space-y-6">
            <div className="neo-card p-6">
              <h3 className="text-lg font-bold mb-6">Conversion Funnel</h3>
              <div className="space-y-4">
                {funnel.funnel?.map((stage, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-40 text-sm font-bold">{stage.stage}</div>
                    <div className="flex-1">
                      <div className="h-8 bg-white/10 relative">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${stage.rate}%` }}
                          transition={{ delay: i * 0.1 }}
                          className="h-full bg-gradient-to-r from-[#C0975C] to-[#C0975C]"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-bold">
                          {stage.rate}%
                        </span>
                      </div>
                    </div>
                    <div className="w-20 text-right">
                      <span className="text-[#C0975C] font-bold">{stage.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Drop-off Analysis */}
            <div className="neo-card p-6">
              <h3 className="text-lg font-bold mb-4">Drop-off Points</h3>
              <div className="space-y-2 text-sm">
                {funnel.funnel?.slice(0, -1).map((stage, i) => {
                  const nextStage = funnel.funnel[i + 1];
                  const dropoff = stage.rate - nextStage.rate;
                  return (
                    <div key={i} className="flex justify-between py-2 border-b border-white/5">
                      <span className="text-[#A3A3A3]">{stage.stage} → {nextStage.stage}</span>
                      <span className={dropoff > 20 ? 'text-red-500' : 'text-[#A3A3A3]'}>
                        -{dropoff.toFixed(1)}% drop
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'events' && (
          <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex items-center gap-4">
              <span className="text-[#A3A3A3]">Period:</span>
              {[7, 14, 30].map(d => (
                <button
                  key={d}
                  onClick={() => setEventDays(d)}
                  className={`px-3 py-1 text-sm ${
                    eventDays === d ? 'bg-[#C0975C] text-black' : 'bg-white/5'
                  }`}
                >
                  {d} days
                </button>
              ))}
            </div>

            {events ? (
              <>
                {/* Event Types */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="neo-card p-6">
                    <h3 className="text-lg font-bold mb-4">Events by Type</h3>
                    <div className="space-y-2">
                      {events.events_by_type?.slice(0, 10).map((e, i) => (
                        <div key={i} className="flex justify-between py-2 border-b border-white/5">
                          <span className="text-[#A3A3A3]">{e._id}</span>
                          <span className="text-[#C0975C] font-bold">{e.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="neo-card p-6">
                    <h3 className="text-lg font-bold mb-4">Page Views</h3>
                    <div className="space-y-2">
                      {events.page_views?.slice(0, 10).map((p, i) => (
                        <div key={i} className="flex justify-between py-2 border-b border-white/5">
                          <span className="text-[#A3A3A3]">{p._id || 'Unknown'}</span>
                          <span className="text-[#C0975C] font-bold">{p.views}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Daily Activity */}
                <div className="neo-card p-6">
                  <h3 className="text-lg font-bold mb-4">Daily Activity</h3>
                  <div className="flex items-end gap-1 h-40">
                    {events.daily_activity?.map((day, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-[#C0975C]" 
                          style={{ height: `${Math.min(100, (day.events / 10) * 100)}%` }}
                        />
                        <span className="text-xs text-[#A3A3A3] mt-1 rotate-45">{day.date?.slice(5)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-[#A3A3A3]">
                <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Loading event data...</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'leads' && leads && (
          <div className="space-y-6">
            {/* Lead Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard icon={Mail} label="Total Leads" value={leads.total_leads} color="#C0975C" />
              <MetricCard icon={UserPlus} label="Converted" value={leads.converted} color="#C0975C" />
              <MetricCard icon={Target} label="Conversion Rate" value={`${leads.conversion_rate}%`} color="#0A1F44" />
              <MetricCard icon={TrendingUp} label="This Week" value={leads.daily_leads?.length || 0} color="#C0975C" />
            </div>

            {/* Leads by Source */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="neo-card p-6">
                <h3 className="text-lg font-bold mb-4">Leads by Source</h3>
                <div className="space-y-3">
                  {leads.by_source?.map((s, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-20 text-sm text-[#A3A3A3] capitalize">{s._id}</span>
                      <div className="flex-1 h-6 bg-white/10">
                        <div 
                          className="h-full bg-[#0A1F44]"
                          style={{ width: `${(s.count / leads.total_leads * 100)}%` }}
                        />
                      </div>
                      <span className="w-10 text-right font-bold">{s.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="neo-card p-6">
                <h3 className="text-lg font-bold mb-4">Recent Leads</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {leads.recent_leads?.map((l, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-white/5 text-sm">
                      <span className="text-[#A3A3A3]">{l.email}</span>
                      <span className="text-[#C0975C] capitalize">{l.source}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'export' && (
          <div className="space-y-6">
            <div className="neo-card p-6">
              <h3 className="text-lg font-bold mb-2">Export Your Data</h3>
              <p className="text-[#A3A3A3] mb-6">Download user data, leads, and analytics for external analysis</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <ExportCard 
                  icon={Users}
                  title="Users (CSV)"
                  description="All user profiles with XP, levels, and activity"
                  onClick={() => handleExport('users')}
                />
                <ExportCard 
                  icon={Mail}
                  title="Leads (CSV)"
                  description="Email leads with source and conversion status"
                  onClick={() => handleExport('leads')}
                />
                <ExportCard 
                  icon={Database}
                  title="Users (JSON)"
                  description="Complete user data in JSON format"
                  onClick={() => handleExport('users_json')}
                />
                <ExportCard 
                  icon={Activity}
                  title="Events (JSON)"
                  description="Last 30 days of event tracking data"
                  onClick={() => handleExport('events')}
                />
              </div>
            </div>

            <div className="neo-card p-6 border-[#0A1F44]">
              <h3 className="text-lg font-bold mb-2 text-[#0A1F44]">API Access</h3>
              <p className="text-[#A3A3A3] mb-4">For custom integrations, use these endpoints:</p>
              <div className="bg-black/50 p-4 font-mono text-sm space-y-1">
                <p className="text-[#A3A3A3]"># Get all users</p>
                <p className="text-[#C0975C]">GET /api/export/users</p>
                <p className="text-[#A3A3A3] mt-2"># Get engagement data</p>
                <p className="text-[#C0975C]">GET /api/export/engagement</p>
                <p className="text-[#A3A3A3] mt-2"># Get event data</p>
                <p className="text-[#C0975C]">GET /api/export/events?days=30</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function SortableTh({ label, field, sort, order, onSort }) {
  const active = sort === field;
  return (
    <th className="px-4 py-3 font-semibold select-none">
      <button
        onClick={() => onSort(field)}
        data-testid={`member-sort-${field}`}
        className={`inline-flex items-center gap-1 hover:text-white transition-colors ${active ? 'text-[#C0975C]' : ''}`}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${active && order === 'asc' ? 'rotate-180' : ''} ${active ? 'opacity-100' : 'opacity-30'}`} />
      </button>
    </th>
  );
}


// Metric Card Component
function MetricCard({ icon: Icon, label, value, subtext, color }) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="neo-card p-4"
    >
      <div className="flex items-start justify-between">
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="mt-3">
        <div className="text-2xl lg:text-3xl font-black" style={{ color }}>{value}</div>
        <div className="text-sm text-white font-bold">{label}</div>
        {subtext && <div className="text-xs text-[#A3A3A3] mt-1">{subtext}</div>}
      </div>
    </motion.div>
  );
}

// Export Card Component
function ExportCard({ icon: Icon, title, description, onClick }) {
  return (
    <button
      onClick={onClick}
      className="neo-card p-6 text-left hover:border-[#C0975C] transition-colors group"
    >
      <Icon className="w-8 h-8 text-[#C0975C] mb-3" />
      <h4 className="font-bold mb-1 group-hover:text-[#C0975C]">{title}</h4>
      <p className="text-sm text-[#A3A3A3]">{description}</p>
      <div className="mt-4 flex items-center gap-2 text-[#C0975C] text-sm">
        <Download className="w-4 h-4" />
        Download
      </div>
    </button>
  );
}
